import { Button, Card, Empty, Input, Menu, message, Select, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import LayoutWithMenu from 'components/Layout/LayoutWithMenu';
import { useScreen } from 'hooks/useScreen';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { queryErrorMessage } from '../CashTransactions/types';
import { AbsentCell, AwardCell, ScoreCell } from './ScoreCells';
import {
  type CompetitionOptions,
  competitionLabel,
  downloadCsv,
  fieldsFor,
  participantName,
  placesByTotal,
  type ScoredParticipant,
  totalScore,
} from './types';

const { Title, Text } = Typography;

// Shared with "Оролцогчид": the last Competition opened on either screen.
const STORAGE_KEY = 'competition_participants.competition_id';

const byText = (a: string | null | undefined, b: string | null | undefined) => {
  if (!a || !b) return a ? -1 : b ? 1 : 0;
  return a.localeCompare(b, 'mn', { numeric: true });
};
const bySortable = (a: number | null | undefined, b: number | null | undefined) =>
  (a ?? Number.NEGATIVE_INFINITY) - (b ?? Number.NEGATIVE_INFINITY);

// "Оноо" (SHINE-68): one Competition class year at a time (each sits its own
// paper), chosen in the left panel: its Participants with a score on each Score
// field of their class year, an Absent mark, the Total score (once every
// field is scored), their place in the class year and an Award level,
// highest total first. Score fields, Award levels and publishing are set on
// the Competition's "Оноо" tab.
export default function CompetitionScores() {
  const { level, runQuery } = useScreen();
  const canEdit = level === 'edit';

  const [options, setOptions] = useState<CompetitionOptions>({ competitions: [], rooms: [] });
  const [competitionId, setCompetitionId] = useState<number | null>(() => {
    try {
      return Number(localStorage.getItem(STORAGE_KEY)) || null;
    } catch {
      return null;
    }
  });
  const [rows, setRows] = useState<ScoredParticipant[]>([]);
  const [loading, setLoading] = useState(false);
  const [classYearId, setClassYearId] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  // Row order, fixed at load: highest Total score first. Typing a score must
  // not move the row being typed in, so it is not re-sorted until reload.
  const [order, setOrder] = useState<Map<number, number>>(new Map());

  useEffect(() => {
    runQuery<CompetitionOptions>('competition_options')
      .then(([row]) => {
        if (!row) return;
        setOptions(row);
        setCompetitionId((current) =>
          current && row.competitions.some((c) => c.id === current)
            ? current
            : (row.competitions[0]?.id ?? null),
        );
      })
      .catch((e) => message.error(queryErrorMessage(e)));
  }, [runQuery]);

  const competition = options.competitions.find((c) => c.id === competitionId) ?? null;
  const isOlympiad = competition?.type === 'OLYMPIAD';
  const scoreFields = competition?.score_fields ?? [];
  const classYears = [...(competition?.class_years ?? [])].sort(
    (a, b) => a.class_number - b.class_number,
  );
  // The class year shown: the one picked, else the first.
  const activeClassId =
    classYears.find((cy) => cy.id === classYearId)?.id ?? classYears[0]?.id ?? null;
  const activeClass = classYears.find((cy) => cy.id === activeClassId) ?? null;
  const columnFields = activeClassId ? fieldsFor(scoreFields, activeClassId) : [];
  const awardLevels = competition?.award_levels ?? [];

  const load = useCallback(() => {
    if (!competitionId) return;
    setLoading(true);
    runQuery<ScoredParticipant>('competition_scores', { competitionId })
      .then((data) => {
        setRows(data);
        const sorted = [...data].sort(
          (a, b) => bySortable(b.total_score, a.total_score) || a.id - b.id,
        );
        setOrder(new Map(sorted.map((p, index) => [p.id, index])));
      })
      .catch((e) => message.error(queryErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [runQuery, competitionId]);

  useEffect(load, [load]);

  const selectCompetition = (id: number) => {
    setCompetitionId(id);
    setClassYearId(null);
    setRows([]);
    try {
      localStorage.setItem(STORAGE_KEY, String(id));
    } catch {
      // Remembering the choice is a convenience only.
    }
  };

  // Place within the class year, from every Participant, not just those shown.
  const places = useMemo(() => placesByTotal(rows), [rows]);

  const shown = useMemo(() => {
    const term = search.trim().toLowerCase();
    return rows
      .filter((p) => p.competition_class_year_id === activeClassId)
      .filter(
        (p) =>
          !term ||
          [p.last_name, p.first_name, p.school_name, p.exam_number]
            .filter(Boolean)
            .some((v) => String(v).toLowerCase().includes(term)),
      )
      .sort((a, b) => (order.get(a.id) ?? 0) - (order.get(b.id) ?? 0));
  }, [rows, order, activeClassId, search]);

  // Left panel: each class year's Participants, and how many still lack a
  // Total score without being Absent.
  const classStats = useMemo(() => {
    const stats = new Map<number, { total: number; unfinished: number }>();
    for (const p of rows) {
      const s = stats.get(p.competition_class_year_id) ?? { total: 0, unfinished: 0 };
      s.total++;
      if (!p.is_absent && p.total_score === null) s.unfinished++;
      stats.set(p.competition_class_year_id, s);
    }
    return stats;
  }, [rows]);

  const absentCount = shown.filter((p) => p.is_absent).length;
  const unfinished = shown.filter((p) => !p.is_absent && p.total_score === null).length;

  // Saves one score or Award level and patches the row in place, its Total
  // score recounted the way the server counts it.
  const save = async (
    id: number,
    change: {
      scores?: Record<string, number | null>;
      award_level_id?: number | null;
      is_absent?: boolean;
    },
  ) => {
    if (!competitionId) return false;
    try {
      await runQuery('competition_scores_save', { competitionId, items: [{ id, ...change }] });
      setRows((current) =>
        current.map((p) => {
          if (p.id !== id) return p;
          const isAbsent = change.is_absent ?? p.is_absent;
          // Marking Absent removes scores and award, as the server does.
          const scores = isAbsent ? {} : { ...p.scores };
          for (const [fieldId, score] of Object.entries(change.scores ?? {})) {
            if (score === null) delete scores[fieldId];
            else scores[fieldId] = score;
          }
          return {
            ...p,
            is_absent: isAbsent,
            scores,
            total_score: isAbsent
              ? null
              : totalScore(scores, fieldsFor(scoreFields, p.competition_class_year_id)),
            award_level_id: isAbsent
              ? null
              : change.award_level_id !== undefined
                ? change.award_level_id
                : p.award_level_id,
          };
        }),
      );
      return true;
    } catch (e) {
      message.error(queryErrorMessage(e));
      return false;
    }
  };

  // Enter in a score cell moves to the same field on the row below, in the
  // order shown.
  const focusNext = (fieldId: number, id: number) => {
    const inputs = [
      ...document.querySelectorAll<HTMLInputElement>(`input[data-score^="${fieldId}:"]`),
    ];
    const index = inputs.findIndex((el) => el.dataset.score === `${fieldId}:${id}`);
    inputs[index + 1]?.focus();
  };

  const awardName = (id: number | null) => awardLevels.find((a) => a.id === id)?.name ?? null;

  const exportCsv = () => {
    if (!competition) return;
    downloadCsv(`${competition.name}-${activeClass?.name ?? ''}-onoo.csv`, [
      [
        'Байр',
        'Овог',
        'Нэр',
        'Тэмцээний анги',
        'Сургууль',
        'Шалгалтын дугаар',
        ...columnFields.map((f) => f.name),
        'Ирээгүй',
        'Нийт оноо',
        'Шагнал',
      ],
      ...shown.map((p) => [
        places.get(p.id) ?? null,
        p.last_name,
        p.first_name,
        p.class_year_name,
        p.school_name,
        p.exam_number,
        ...columnFields.map((f) => p.scores[f.id] ?? null),
        p.is_absent ? 'Тийм' : '',
        p.total_score,
        awardName(p.award_level_id),
      ]),
    ]);
  };

  const columns: ColumnsType<ScoredParticipant> = [
    {
      title: 'Байр',
      key: 'place',
      fixed: 'left',
      width: 60,
      align: 'center',
      render: (_: unknown, p) => places.get(p.id) ?? '—',
    },
    {
      title: 'Овог нэр',
      key: 'name',
      fixed: 'left',
      width: 190,
      sorter: (a, b) => byText(participantName(a), participantName(b)),
      render: (_: unknown, p) => <Text strong>{participantName(p)}</Text>,
    },
    {
      title: 'Сургууль',
      dataIndex: 'school_name',
      width: 170,
      sorter: (a, b) => byText(a.school_name, b.school_name),
      render: (v: string | null) => v ?? '—',
    },
    {
      title: 'Дугаар',
      dataIndex: 'exam_number',
      width: 80,
      sorter: (a, b) => byText(a.exam_number, b.exam_number),
      render: (v: string | null) => v ?? '—',
    },
    ...columnFields.map((f) => ({
      title: (
        <span>
          {f.name}
          <Text type="secondary" style={{ display: 'block', fontSize: 11, fontWeight: 400 }}>
            {f.min_score}–{f.max_score}
          </Text>
        </span>
      ),
      key: `score-${f.id}`,
      width: 100,
      sorter: (a: ScoredParticipant, b: ScoredParticipant) =>
        bySortable(a.scores[f.id], b.scores[f.id]),
      // A field of another class year has no cell on this row.
      render: (_: unknown, p: ScoredParticipant) =>
        !f.class_year_ids.includes(p.competition_class_year_id) ? null : canEdit ? (
          <ScoreCell
            participantId={p.id}
            field={f}
            value={p.scores[f.id] ?? null}
            disabled={p.is_absent}
            onSave={(score) => save(p.id, { scores: { [f.id]: score } })}
            onNext={() => focusNext(f.id, p.id)}
          />
        ) : (
          (p.scores[f.id] ?? '—')
        ),
    })),
    {
      title: 'Нийт',
      key: 'total',
      fixed: 'right',
      width: 80,
      align: 'center',
      sorter: (a, b) => bySortable(a.total_score, b.total_score),
      render: (_: unknown, p) =>
        p.is_absent ? (
          <Text type="secondary">Ирээгүй</Text>
        ) : p.total_score !== null ? (
          <Text strong>{p.total_score}</Text>
        ) : Object.keys(p.scores).length ? (
          <Tag color="orange" style={{ marginInlineEnd: 0 }}>
            дутуу
          </Tag>
        ) : (
          '—'
        ),
    },
    {
      title: 'Ирээгүй',
      key: 'absent',
      fixed: 'right',
      width: 70,
      align: 'center',
      render: (_: unknown, p) =>
        canEdit ? (
          <AbsentCell
            value={p.is_absent}
            hasScores={Object.keys(p.scores).length > 0 || p.award_level_id !== null}
            disabled={false}
            onSave={(is_absent) => save(p.id, { is_absent })}
          />
        ) : p.is_absent ? (
          'Тийм'
        ) : (
          ''
        ),
    },
    {
      title: 'Шагнал',
      key: 'award',
      fixed: 'right',
      width: 140,
      render: (_: unknown, p) =>
        canEdit ? (
          <AwardCell
            value={p.award_level_id}
            awardLevels={awardLevels}
            // Only once the Participant has a Total score (CONTEXT.md).
            disabled={!awardLevels.length || p.total_score === null}
            onSave={(award_level_id) => save(p.id, { award_level_id })}
          />
        ) : (
          (awardName(p.award_level_id) ?? '—')
        ),
    },
  ];

  return (
    <LayoutWithMenu className="px-3 lg:px-5">
      <Card>
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 12,
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 16,
          }}
        >
          <Title level={4} style={{ margin: 0 }}>
            Оноо
          </Title>
          {competition && (
            <Tag color={competition.results_published ? 'green' : 'default'}>
              {competition.results_published
                ? `Үр дүн нийтлэгдсэн${competition.results_limit ? ` · анги бүрээс эхний ${competition.results_limit}` : ''}`
                : 'Үр дүн нийтлээгүй'}
            </Tag>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
          <Select
            style={{ minWidth: 280, flex: '1 1 280px', maxWidth: 640 }}
            placeholder="Тэмцээн сонгох"
            value={competitionId ?? undefined}
            onChange={selectCompetition}
            showSearch
            optionFilterProp="label"
            options={options.competitions.map((c) => ({
              value: c.id,
              label: competitionLabel(c),
            }))}
          />
          <Input.Search
            style={{ maxWidth: 260 }}
            placeholder="Нэр, сургууль, дугаар..."
            allowClear
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Button onClick={exportCsv} disabled={!shown.length}>
            CSV татах
          </Button>
        </div>

        {!competition ? (
          <Empty description="Тэмцээн сонгоно уу." />
        ) : !isOlympiad ? (
          <Empty description="Оноо зөвхөн олимпиадад оруулна." />
        ) : !scoreFields.length ? (
          <Empty description='Онооны талбар тохируулаагүй байна. Тэмцээн → "Оноо" табаас нэмнэ үү.' />
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-start' }}>
            <Menu
              mode="inline"
              style={{ width: 200, flexShrink: 0, borderInlineEnd: 'none' }}
              selectedKeys={activeClassId ? [String(activeClassId)] : []}
              onClick={({ key }) => setClassYearId(Number(key))}
              items={classYears.map((cy) => {
                const st = classStats.get(cy.id) ?? { total: 0, unfinished: 0 };
                return {
                  key: String(cy.id),
                  style: { height: 'auto', lineHeight: 1.4, paddingBlock: 8 },
                  label: (
                    <div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                        <span>{cy.name}</span>
                        <Text type="secondary">{st.total}</Text>
                      </div>
                      {st.total > 0 && (
                        <Text type={st.unfinished ? 'warning' : 'success'} style={{ fontSize: 12 }}>
                          {st.unfinished ? `Оноо дутуу ${st.unfinished}` : 'Бүгд оноотой'}
                        </Text>
                      )}
                    </div>
                  ),
                };
              })}
            />
            <div style={{ flex: '1 1 480px', minWidth: 0 }}>
              <div
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  alignItems: 'center',
                  marginBottom: 12,
                }}
              >
                <Text strong>{activeClass?.name}</Text>
                <Text type="secondary">{shown.length} оролцогч</Text>
                {shown.length > 0 && (
                  <>
                    <Tag color={unfinished ? 'orange' : 'green'}>
                      {unfinished ? `Оноо дутуу ${unfinished}` : 'Бүгд оноотой'}
                    </Tag>
                    {absentCount > 0 && <Tag>Ирээгүй {absentCount}</Tag>}
                  </>
                )}
              </div>
              {columnFields.length ? (
                <Table
                  rowKey="id"
                  size="small"
                  columns={columns}
                  dataSource={shown}
                  loading={loading}
                  scroll={{ x: 'max-content' }}
                  pagination={{
                    defaultPageSize: 50,
                    showSizeChanger: true,
                    showTotal: (total) => `Нийт ${total}`,
                  }}
                />
              ) : (
                <Empty description='Энэ ангид онооны талбар тохируулаагүй байна. Тэмцээн → "Оноо" табаас нэмнэ үү.' />
              )}
            </div>
          </div>
        )}
      </Card>
    </LayoutWithMenu>
  );
}
